import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	OnInit,
	signal,
	Signal,
} from '@angular/core';
import { ESigningProvider } from '@models/e-signing';
import { dispatch, select, Store } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ContentHeaderTitleImg } from 'src/app/ui/content-header/content-header.component';
import {
	BusinessFeeSettingsFirestorePaths,
	BusinessSettingsCardProvidersFirestorePaths,
} from '../business-finances-general/business-finances-general.types';
import { BaseBusinessSettingsSection } from '../business-finances-general/common/base-business-settings-section/base-business-settings-section.directive';
import { TestHooks } from './e-signing-general.hooks';
import { DirectionOfFeeType } from '@models/businessSettings/directionOfFees';
import { SignItConnectionFlowService } from 'src/app/shared/services/signit-connection-flow/signit-connection-flow.service';
import { OverwriteFields } from 'src/app/shared/state/user-profile/actions';
import { UserProfileState } from '@state/user-profile';
import { ALERT_TYPES } from 'src/app/ui/alert/alert.component';
import { OverwriteBusinessFields } from 'src/app/shared/state/business/business.actions';

@Component({
	selector: 'app-e-signing-general',
	templateUrl: './e-signing-general.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ESigningGeneralComponent
	extends BaseBusinessSettingsSection
	implements OnInit
{
	testHooks = TestHooks;
	private store = inject(Store);
	private signItConnectService = inject(SignItConnectionFlowService);
	private OverwriteFields = dispatch(OverwriteFields);
	private overwriteBusinessFields = dispatch(OverwriteBusinessFields);

	readonly getDefaultNote = select(BusinessState.getDefaultNote);
	readonly isSignItConnected = select(BusinessState.isSignITSelected);
	readonly hasDisconnected = select(BusinessState.hasSignITDisconnected);
	readonly displayNoteClosed = select(
		UserProfileState.isESigningProviderNoteHidden,
	);
	readonly ALERT_TYPES = ALERT_TYPES;

	providerName = computed(() =>
		this.isSignItConnected() ? 'SignIT powered by Docusign' : 'Not Connected',
	);

	buttonState = computed(() =>
		this.isSignItConnected() ? 'Disconnect' : 'Connect to SignIT',
	);

	showSignItNote = computed(
		() =>
			!this.displayNoteClosed() &&
			!this.isSignItConnected() &&
			!this.hasDisconnected(),
	);

	public defaultESigningSettings_ = toSignal(
		inject(Store)
			.select(BusinessState.getDefaultESigningSettings)
			.pipe(map((settings) => settings?.feeSettings)),
	);

	public showFinancialSettings_ = toSignal(
		this.store
			.select(BusinessState.getESigningProviderSelected)
			.pipe(map((provider) => provider !== ESigningProvider.MANUAL)),
	);

	public defaultProvider_ = toSignal(
		inject(Store).select(BusinessState.getESigningProviderSelected),
	);

	public showFeeAndHstSettings_ = computed(() => {
		return (
			this.defaultESigningSettings_()?.location !== DirectionOfFeeType.NONE
		);
	});

	public titleImgConfig_: Signal<ContentHeaderTitleImg> = computed(() => {
		const currESigningProvider = this.defaultProvider_();

		const matchedProvider = this.eSigningProviders.find(
			(option) => option.value === currESigningProvider,
		);

		if (!matchedProvider || !matchedProvider.imagePath) return null;

		return {
			imgSrc: matchedProvider.imagePath,
			altText: matchedProvider.label,
			classes: matchedProvider.classes || '',
		};
	});

	public firestoreDefaultSettingsPath =
		BusinessFeeSettingsFirestorePaths.ESigning;

	public eSigningProviders = [
		{
			value: ESigningProvider.SIGN_IT,
			imagePath: '/assets/images/integrations/sign-it/signIt-isolated.png',
			label: 'SignIT',
			classes: 'max-h-6',
		},
		{
			value: ESigningProvider.MANUAL,
			label: 'External Provider',
		},
	];

	notes = signal<string>('');

	ngOnInit(): void {
		this.notes.set(this.getDefaultNote());
	}

	openModal(): void {
		switch (this.buttonState()) {
			case 'Connect to SignIT':
				this.signItConnectService.openEducationalModal();
				break;
			case 'Disconnect':
				this.signItConnectService.disconnectFromSignIt();
				break;
		}
	}

	setDefaultProvider(provider: ESigningProvider): void {
		const currProvider = this.defaultProvider_();

		if (provider === currProvider) return;

		const fieldToUpdate = BusinessSettingsCardProvidersFirestorePaths.ESigning;
		this.overwriteBusinessFields({
			field: `defaultSettings.${fieldToUpdate}.defaultProvider`,
			value: provider,
		});
	}

	setAmount(amount: number): void {
		this.overwriteBusinessFields({
			field: `defaultSettings.${this.firestoreDefaultSettingsPath}.amount`,
			value: amount,
		});
	}

	setHst(isHst: boolean): void {
		this.overwriteBusinessFields({
			field: `defaultSettings.${this.firestoreDefaultSettingsPath}.hst`,
			value: isHst,
		});
	}

	signItNavigationUrl(): void {
		window.open('https://www.closer.ca/features/e-signing', '_blank');
	}

	hideSignItNote() {
		this.OverwriteFields({
			field: 'settings.hideNotifications.hideESigningProviderNote',
			value: true,
		});
	}

	onNoteDebounced(value: string) {
		this.overwriteBusinessFields({
			field: 'notes',
			value: value,
		});
	}
}
